import { useEffect, useRef } from "react";
import { Link as RouterLink, Navigate, useParams, useSearchParams } from "react-router-dom";
import { Box, Button, CircularProgress, Stack, Typography, useTheme } from "@mui/material";
import ErrorDisplay from "../common/ErrorDisplay/ErrorDisplay";
import { INBOX_PATH } from "../../lib/notificationTarget";
import { useNotificationsContext } from "./NotificationsProvider";
import { useNotification } from "./hooks/useNotification";
import { NOTIFICATION_UNAVAILABLE } from "./notificationUnavailable";
import { NotificationOpenSource } from "./types";

const sourceFromParam = (value: string | null): NotificationOpenSource => {
  if (value === "email" || value === "push") return value;
  return "link";
};

/**
 * A withdrawn notification's link: neutral and inside the app shell. Its
 * content is never shown, and nothing here reads as an error — there is
 * nothing to retry.
 */
const NotificationUnavailable = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 720,
        mx: "auto",
        px: 2,
        pt: { xs: 8, md: 12 },
        pb: 8,
        textAlign: "center",
      }}
    >
      <Typography
        component="h1"
        sx={{ fontSize: "1.125rem", fontWeight: 600, color: theme.palette.text.primary }}
      >
        This alert is no longer available
      </Typography>
      <Typography sx={{ mt: 0.75, fontSize: "0.875rem", color: theme.palette.text.secondary }}>
        Your other notifications are in your inbox.
      </Typography>
      <Button
        component={RouterLink}
        to={INBOX_PATH}
        replace
        variant="outlined"
        sx={{
          mt: 3,
          minHeight: 44,
          px: 3,
          borderRadius: 2,
          textTransform: "none",
          fontWeight: 600,
          color: theme.palette.text.primary,
          borderColor: isDark ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)",
          "&:hover": {
            borderColor: isDark ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.3)",
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        Go to inbox
      </Button>
    </Box>
  );
};

/**
 * /n/:id — where notification emails and Universal Links land. A waypoint, not
 * a page: it confirms the notification is this user's, then opens it exactly
 * as a row tap would and REPLACES itself in history, so back never returns
 * here.
 *
 * The GET marks nothing read (mail scanners and link previews fetch URLs); the
 * read happens in the authenticated POST inside openNotification. An id that
 * no longer resolves — pruned, someone else's, malformed — opens the inbox. A
 * withdrawn one stays here on a neutral "no longer available" state, and the
 * open path lands here too when its POST finds the item withdrawn.
 */
const NotificationLinkPage = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { openNotification } = useNotificationsContext();
  const { data, isLoading, error, refetch } = useNotification(id);

  const source = sourceFromParam(searchParams.get("s"));
  const unavailable = data === NOTIFICATION_UNAVAILABLE;
  const itemId = data && data !== NOTIFICATION_UNAVAILABLE ? data.id : undefined;

  // Once per notification: StrictMode re-runs effects, and a second POST would
  // send a second navigation.
  const openedIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!itemId || openedIdRef.current === itemId) return;
    openedIdRef.current = itemId;
    openNotification(itemId, source, { replace: true });
  }, [itemId, source, openNotification]);

  if (!id || (!isLoading && !error && data === null)) {
    return <Navigate to={INBOX_PATH} replace />;
  }

  if (unavailable) return <NotificationUnavailable />;

  if (error) {
    return (
      <Box sx={{ width: "100%", maxWidth: 720, mx: "auto", px: 2, pt: 4, pb: 8 }}>
        <ErrorDisplay error={error} onRetry={() => void refetch()} variant="card" fullWidth />
      </Box>
    );
  }

  return (
    <Stack alignItems="center" justifyContent="center" sx={{ minHeight: "50vh" }}>
      <CircularProgress size={28} />
    </Stack>
  );
};

export default NotificationLinkPage;
